import { Link, Tooltip, Typography } from '@mui/material';
import { useCallback } from 'react';
import { Column } from 'react-data-grid';
import { navigateToSpecificApp } from '../../common/routing/app-navigator';
import { splitLongNames } from 'string-utils';
import CustomLink from '../../elements/Aggregation/elements/Link';
import MultiLineEllipsis from '../../elements/MultiLineEllipsis/MultiLineEllipsis';
import { SbomLibraryItem } from '../sbom-types';
import IssueBadge from '../../common/components/IssueBadge';

export const useSbomHeader = (classes?: {
  textCenter?: string;
  flexCenter?: string;
  headerCell?: string;
  noPadding?: string;
  columnsContainer?: string;
  textOverFlow?: string;
}) => {
  const baseColumn: Partial<Column<SbomLibraryItem>> = {
    headerCellClass: classes?.headerCell,
  };

  const openApps = useCallback(
    (
      event: React.MouseEvent<HTMLAnchorElement> &
        React.MouseEvent<HTMLSpanElement>,
    ) => {
      const { appid } = event.currentTarget.dataset;
      appid && navigateToSpecificApp(appid);
      event.stopPropagation();
      event.preventDefault();
    },
    [],
  );

  const columns: Column<SbomLibraryItem>[] = [
    {
      ...baseColumn,
      key: 'pkgName',
      name: 'Package Name',
      formatter: ({ row }) => (
        <CustomLink
          href={row.libLink}
          value={row.pkgName}
          textClassName={classes?.textOverFlow}
        />
      ),
      width: 200,
    },
    {
      ...baseColumn,
      key: 'libraryName',
      name: 'Library Name',
      formatter: ({ row }) => (
        <MultiLineEllipsis variant='caption' lines={2}>
          {row.libraryName}
        </MultiLineEllipsis>
      ),
      width: 180,
    },

    {
      ...baseColumn,
      key: 'libraryVersion',
      name: 'Version',
      width: 110,
      formatter: ({ row }) => (
        <MultiLineEllipsis variant='caption' lines={3}>
          {row.libraryVersion}
        </MultiLineEllipsis>
      ),
    },
    {
      ...baseColumn,
      key: 'license',
      name: 'License',
      width: 120,
      formatter: ({ row }) => (
        <CustomLink
          href={row.copyWriteInfoLink}
          value={row.license}
          textClassName={classes?.textOverFlow}
        />
      ),
    },
    {
      ...baseColumn,
      key: 'copyRight',
      name: 'Copyright',
      width: 150,
      formatter: ({ row }) => (
        <div className={classes?.columnsContainer}>
          {row.copyWriteInfo &&
            row.copyWriteInfo[0] &&
            (row.copyWriteInfo[0] === 'N/A' ? (
              <Typography variant='body2' color='text.primary' mb={0.5}>
                {row.copyWriteInfo[0]}
              </Typography>
            ) : (
              <Tooltip
                title={row.copyWriteInfo.map((copyRight, index) => {
                  return (
                    <Typography
                      variant='body2'
                      color='text.primary'
                      key={index}
                      mb={0.5}>
                      {copyRight}
                    </Typography>
                  );
                })}>
                <Typography
                  variant='body2'
                  color='text.primary'
                  className={classes?.textOverFlow}>
                  {row.copyWriteInfo[0]}
                </Typography>
              </Tooltip>
            ))}
        </div>
      ),
    },
    {
      ...baseColumn,
      key: 'appName',
      name: 'App Name',
      width: 200,
      formatter: ({ row }) => (
        <Link onClick={openApps} data-appid={row.appId}>
          {splitLongNames(row.appName, 30)}
        </Link>
      ),
    },
    {
      ...baseColumn,
      key: 'dependencyType',
      name: 'Dependency',
      width: 100,
    },

    {
      ...baseColumn,
      key: 'location',
      name: 'Location',
      minWidth: 200,
      formatter: ({ row }) => (
        <Tooltip
          placement='top'
          arrow
          title={<Typography variant='caption'>{row.location}</Typography>}>
          <div>
            <MultiLineEllipsis lines={3}>
              <CustomLink
                variant='caption'
                href={row.locationLink}
                value={splitLongNames(row.location, 20)}
              />
            </MultiLineEllipsis>
          </div>
        </Tooltip>
      ),
    },
    {
      ...baseColumn,
      key: 'source',
      name: 'Source',
      width: 100,
      formatter: ({ row }) => (
        <CustomLink variant='caption' href={row.appLink} value={row.source} />
      ),
    },
    {
      ...baseColumn,
      key: 'issues',
      name: 'Issues',
      width: 170,
      formatter: ({ row }) => {
        return (
          <IssueBadge top={3} severitiesAlerts={row.vulnerabilityCounts} />
        );
      },
    },
  ];

  return columns;
};
